// src/config/sentry.ts

import * as Sentry from '@sentry/react';

type Severity = 'medium' | 'high' | 'critical';

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
];

let pendingSeverity: Severity = 'high';
let discardFeedbackDraftOnClose = false;
let feedbackDraft = {
  name: '',
  email: '',
  message: '',
  severity: 'high' as Severity,
};

function getFeedbackShadowRoot(): ShadowRoot | null {
  const host = document.querySelector('#sentry-feedback');
  return (host as HTMLElement | null)?.shadowRoot ?? null;
}

function getFeedbackField<T extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
  root: ShadowRoot,
  id: string,
): T | null {
  return root.querySelector(`#${id}`) as T | null;
}

function hasFeedbackDraft(draft = feedbackDraft) {
  return Boolean(
    draft.name.trim() || draft.email.trim() || draft.message.trim() || draft.severity !== 'high',
  );
}

function clearFeedbackDraft() {
  feedbackDraft = { name: '', email: '', message: '', severity: 'high' };
  pendingSeverity = 'high';
  discardFeedbackDraftOnClose = false;
}

const readFeedbackDraft = (root: ShadowRoot) => ({
  name: getFeedbackField<HTMLInputElement>(root, 'name')?.value ?? '',
  email: getFeedbackField<HTMLInputElement>(root, 'email')?.value ?? '',
  message: getFeedbackField<HTMLTextAreaElement>(root, 'message')?.value ?? '',
  severity:
    (getFeedbackField<HTMLSelectElement>(root, 'compass-severity')?.value as
      | Severity
      | undefined) ?? pendingSeverity,
});

function persistFeedbackDraft(root = getFeedbackShadowRoot()) {
  if (!root) return;

  feedbackDraft = readFeedbackDraft(root);
  pendingSeverity = feedbackDraft.severity;
}

function restoreFeedbackDraft(root: ShadowRoot) {
  if (!hasFeedbackDraft()) return;

  const nameInput = getFeedbackField<HTMLInputElement>(root, 'name');
  const emailInput = getFeedbackField<HTMLInputElement>(root, 'email');
  const messageInput = getFeedbackField<HTMLTextAreaElement>(root, 'message');
  const severityInput = getFeedbackField<HTMLSelectElement>(root, 'compass-severity');

  if (nameInput) nameInput.value = feedbackDraft.name;
  if (emailInput) emailInput.value = feedbackDraft.email;
  if (messageInput) messageInput.value = feedbackDraft.message;
  if (severityInput) severityInput.value = feedbackDraft.severity;
  pendingSeverity = feedbackDraft.severity;
}

function attachFeedbackDraftListeners(root: ShadowRoot) {
  const form = root.querySelector('form');
  if (form && form.dataset.compassDraftListener !== 'true') {
    form.dataset.compassDraftListener = 'true';
    form.addEventListener('input', () => persistFeedbackDraft(root));
    form.addEventListener('change', () => persistFeedbackDraft(root));
  }

  const cancelButton = root.querySelector('.btn-group button[type="button"]');
  if (
    cancelButton instanceof HTMLButtonElement &&
    cancelButton.dataset.compassDraftListener !== 'true'
  ) {
    cancelButton.dataset.compassDraftListener = 'true';
    cancelButton.addEventListener(
      'click',
      () => {
        discardFeedbackDraftOnClose = true;
      },
      { capture: true },
    );
  }
}

function buildSeverityField(labelClass: string, inputClass: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.id = 'compass-severity-field';
  wrapper.style.cssText = 'display:flex;flex-direction:column;';

  const label = document.createElement('label');
  label.className = labelClass;
  label.setAttribute('for', 'compass-severity');
  label.textContent = 'Severity';

  const select = document.createElement('select');
  select.className = inputClass;
  select.id = 'compass-severity';
  // Native selects need a bit of extra inline styling to look consistent.
  select.style.cssText =
    'appearance:auto;-webkit-appearance:auto;width:100%;box-sizing:border-box;cursor:pointer;';
  for (const opt of SEVERITY_OPTIONS) {
    const o = document.createElement('option');
    o.value = opt.value;
    o.textContent = opt.label;
    if (opt.value === pendingSeverity) o.selected = true;
    select.appendChild(o);
  }
  select.addEventListener('change', () => {
    pendingSeverity = select.value as Severity;
  });

  wrapper.appendChild(label);
  wrapper.appendChild(select);
  return wrapper;
}

function prepareFeedbackForm() {
  requestAnimationFrame(() => {
    const root = getFeedbackShadowRoot();
    if (!root) return;

    const messageLabel = root.querySelector('label[for="message"]') as HTMLElement | null;
    const messageInput = root.querySelector('#message') as HTMLElement | null;
    if (!messageLabel || !messageInput) return;

    if (!root.querySelector('#compass-severity-field')) {
      const wrapper = buildSeverityField(messageLabel.className, messageInput.className);

      // Insert after the message field's wrapper so order is Name -> Email -> Message -> Severity.
      const messageWrapper = messageLabel.parentElement;
      messageWrapper?.parentElement?.insertBefore(wrapper, messageWrapper?.nextSibling ?? null);
    }

    restoreFeedbackDraft(root);
    attachFeedbackDraftListeners(root);
  });
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Call this before rendering the React app
 */
export function initializeSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'dev';
  const release = process.env.REACT_APP_SENTRY_RELEASE || 'unknown';

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Set sample rate for production
    tracesSampleRate: environment === 'prod' ? 0.1 : 1.0,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Capture console errors
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),

      // Feedback widget - enabled in all environments
      Sentry.feedbackIntegration({
        colorScheme: 'light',
        isNameRequired: true,
        isEmailRequired: true,
        showBranding: false,
        autoInject: false, // Don't auto-inject the default button, we use a custom button
        onFormOpen: prepareFeedbackForm,
        onFormClose: () => {
          if (discardFeedbackDraftOnClose) {
            clearFeedbackDraft();
          } else {
            persistFeedbackDraft();
          }
        },
        onSubmitSuccess: () => {
          clearFeedbackDraft();
        },
      }),
    ],

    // Filter out sensitive data
    beforeSend(event) {
      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      // Filter out noise from browser extensions
      if (event.exception?.values?.[0]?.value?.includes('chrome-extension://')) {
        return null;
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extension errors
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // Auth0 expected errors
      'login_required',
      'consent_required',
    ],

    // Tag all events with user context
    initialScope: {
      tags: {
        app: 'compass-dashboard',
      },
    },
  });

  // Attach severity tag to user feedback events only.
  Sentry.addEventProcessor(event => {
    if (event.type === 'feedback') {
      event.tags = { ...event.tags, severity: pendingSeverity };
    }
    return event;
  });

  // eslint-disable-next-line no-console
  console.info(`Sentry initialized in ${environment} environment`);
}

/**
 * Set user context for Sentry
 * Call this after user authentication
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Clear user context from Sentry
 * Call this on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add custom breadcrumb to Sentry
 * Useful for tracking user actions
 */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>,
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Capture exception manually
 * Use for caught errors that should be reported
 */
export function captureSentryException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture message manually
 * Use for non-error events
 */
export function captureSentryMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>,
) {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}
